// utilities/orderSummary.js
// ─────────────────────────────────────────────────────────────────────────────
// buildOrderSummary — the money on an order, worked out in ONE place.
//
// This is the dashboard's copy of the helper that already governs the customer
// web client (Lusuku_Web/src/utils/orderSummary.js, Aug 2026). It exists here
// because the dashboard's order-details screens carried the SAME defect the web
// client was fixed for, and a second hand-rolled sum is how the two surfaces
// drift apart again.
//
// THE DEFECT. "Shipping Fees" read
// `shippingAddress.selectedVillage.shippingCost` and `shippingCost_id.cost` —
// both the LEGACY flat zone fee. models/Orders.js marks `shippingCost_id`
// "@deprecated ... do NOT write new logic against it" and documents
// `deliveryFee` as authoritative: server-computed at order creation from the
// signed quote, and the number the customer was actually charged. Nothing on
// the screen rendered `deliveryFee`, so on a fee-engine order the line simply
// went blank and the difference between Cart Total and Total Order Cost had
// nowhere to live. Order 6ab87a8c07d7ea2ae7b1199b showed 70,000 and 76,500
// with an empty Shipping Fees between them.
//
// THREE RULES THIS ENCODES
//
//   1. Authoritative first, legacy only as a fallback. Orders placed before the
//      fee engine existed never had `deliveryFee` written, and for those the
//      legacy pair is still the only truth.
//   2. Whatever the parts do not explain becomes `adjustment`, which the screen
//      RENDERS. A summary that quietly refuses to add up is precisely how the
//      original bug survived; one that names the gap cannot hide the next one.
//   3. VAT is INCLUSIVE and is therefore NOT one of the parts. Prices on this
//      platform carry tax inside them: the server writes
//      `totalPrice = itemsPrice + deliveryFee (+ any processing fee)` and
//      records `taxPrice` as the tax already sitting inside that. Adding it to
//      the parts made every taxed order show a phantom `adjustment` of exactly
//      minus the VAT, which the screen then labelled "Discount".
//
// WATCH FOR A THIRD NAME. `VendorOrderSchema` (the per-vendor sub-document in
// models/Orders.js) declares `shippingCost` with `alias: "deliveryFee"`. So on
// a vendor SUB-order `deliveryFee` resolves to the legacy field, while on the
// Order itself it is the authoritative one. Same word, two meanings, one file.
// Pass the Order, not a vendor sub-order.
// ─────────────────────────────────────────────────────────────────────────────

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const buildOrderSummary = (order) => {
  const itemsPrice = num(order?.itemsPrice);
  const taxPrice = num(order?.taxPrice);
  const totalPrice = num(order?.totalPrice);

  const legacyFee =
    num(order?.shippingAddress?.selectedVillage?.shippingCost) ||
    num(order?.shippingCost_id?.cost);
  const storedFee = num(order?.deliveryFee);
  const deliveryFee = storedFee > 0 ? storedFee : legacyFee;

  /* The customer-facing payment processing fee, present only while Lusuku
     passes the payment-rail charge on rather than absorbing it. Like the
     delivery fee it is CONTAINED IN totalPrice, so it is one of the parts and
     must be named, or it surfaces as an unexplained "Other charges". */
  const processingFee = num(order?.paymentProcessingFee);
  const processingLabel =
    (order?.paymentRail?.feeLabel || "").trim() || "Payment processing fee";

  // Tax is deliberately absent: it is inside itemsPrice, not beside it.
  const adjustment = totalPrice - (itemsPrice + deliveryFee + processingFee);

  // UNITS, not lines. Counting `orderItems.length` reports one line of qty 2
  // as "1 items" — the wrong number and the wrong grammar in four characters.
  const unitCount = (order?.orderItems || []).reduce((n, item) => {
    const q = num(item?.qty);
    return n + (q > 0 ? q : 1);
  }, 0);

  const lineCount = (order?.orderItems || []).length;

  return {
    itemsPrice,
    taxPrice,
    taxIncluded: true,
    deliveryFee,
    deliveryFeeIsLegacy: storedFee <= 0 && legacyFee > 0,
    processingFee,
    processingLabel,
    totalPrice,
    adjustment,
    unitCount,
    lineCount,
    itemsLabel: `${unitCount} item${unitCount === 1 ? "" : "s"}`,
  };
};

/** Thousands-separated amount. Shillings have no cents, so no decimals. */
export const formatAmount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return Math.round(n).toLocaleString("en-US");
};

/** The same amount with the platform's currency word in front of it. */
export const formatMoney = (value, currency = "Ush") =>
  `${currency} ${formatAmount(value)}`;

export default buildOrderSummary;
